import { isSoloPersonalOrganisation } from "./courseDemo";

export type AppView = "practitioner" | "manager";

export const APP_VIEW_STORAGE_KEY_PREFIX = "verbal.activeView";

export const appViewStorageKey = (userId: number): string => `${APP_VIEW_STORAGE_KEY_PREFIX}.${userId}`;

export type AppViewUser = {
    id?: number;
    isSchool?: boolean;
    Role?: { name?: string } | null;
};

export type AppViewOrganisation = {
    orgType?: string | null;
    licenceEndDate?: Date | string | null;
    Tier?: { tier?: string | null } | null;
};

/** Course-demo trainees are Admin of an Individual org — no view switcher, Demo or Full. */
export const isSoloPersonalAdmin = (user?: AppViewUser | null, organisation?: AppViewOrganisation | null): boolean => {
    if (user?.Role?.name !== "Admin") {
        return false;
    }
    return isSoloPersonalOrganisation(organisation, user?.isSchool);
};

export const canSwitchAppViews = (user?: AppViewUser | null, organisation?: AppViewOrganisation | null): boolean => {
    if (!user || user.isSchool) {
        return false;
    }
    if (isSoloPersonalOrganisation(organisation, user.isSchool)) {
        return false;
    }
    const role = user.Role?.name;
    if (role === "ProgrammeCoordinator") {
        return true;
    }
    if (role === "Admin" && !isSoloPersonalAdmin(user, organisation)) {
        return true;
    }
    return false;
};

export const defaultAppView = (user?: AppViewUser | null, organisation?: AppViewOrganisation | null): AppView => {
    if (isSoloPersonalAdmin(user, organisation)) {
        return "practitioner";
    }
    const role = user?.Role?.name;
    if (role === "ProgrammeCoordinator" || (role === "Admin" && !user?.isSchool)) {
        return "manager";
    }
    return "practitioner";
};

export const canUseAppView = (
    view: AppView,
    user?: AppViewUser | null,
    organisation?: AppViewOrganisation | null,
): boolean => {
    if (view === "practitioner") {
        return true;
    }
    return canSwitchAppViews(user, organisation);
};

export const resolveActiveAppView = (
    saved: AppView | null | undefined,
    user?: AppViewUser | null,
    organisation?: AppViewOrganisation | null,
): AppView => {
    const fallback = defaultAppView(user, organisation);
    if (!saved || !canUseAppView(saved, user, organisation)) {
        return fallback;
    }
    return saved;
};

export const parseStoredAppView = (value: string | null | undefined): AppView | null => {
    if (value === "practitioner" || value === "manager") {
        return value;
    }
    return null;
};

export const isDeliveryNavItem = (id: string): boolean => id === "Prepare" || id === "Deliver";

/** School nav keeps Prepare/Deliver. Assess is Phase 3 and is not shipped to schools. */
export const shouldShowNavItem = (id: string, activeView: AppView, isSchool?: boolean): boolean => {
    if (isSchool) {
        return id !== "Assess";
    }
    if (activeView === "manager" && isDeliveryNavItem(id)) {
        return false;
    }
    return true;
};
